import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Autocomplete, TextField, FormControl } from '@mui/material'
import { FC } from 'react'
import { Controller } from 'react-hook-form'
import { IMUIAutoCompleteSingleSelect } from '../../../Models'
import ChipButton from '../ChipButton'

const MUIAutocompleteSingleSelect: FC<IMUIAutoCompleteSingleSelect> = ({
  options = [],
  error,
  label,
  name,
  control,
  rules,
  labelKey = 'label',
  valueKey = 'value',
  required = false,
  disabled = false,
  defaultValueIndex = Number.NaN,
  startIcon,
  searchOnly,
  placeholder,
  onChange,
  statusKey,
  statusLabel,
}) => {
  const formControlStyle: {
    textAlign: string
    position: string
    backgroundColor?: string
    borderRadius?: string
  } = { textAlign: 'left', position: 'relative' }

  if (name === 'parameterSearch') {
    formControlStyle['backgroundColor'] = 'white'
    formControlStyle['borderRadius'] = '5px'
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const textFieldStyle: {
          '& .MuiInputBase-root'?: { fontWeight?: string; borderRadius?: string }
        } = {}

        if (field.value && searchOnly) {
          textFieldStyle['& .MuiInputBase-root'] = {
            fontWeight: '600',
          }
        }

        if (name === 'parameterSearch') {
          textFieldStyle['& .MuiInputBase-root'] = {
            ...textFieldStyle['& .MuiInputBase-root'],
            borderRadius: formControlStyle.borderRadius,
          }
        }

        return (
          <FormControl
            className='c-select'
            fullWidth
            data-testid='select-control'
            error={!!error?.[name]}
            sx={formControlStyle}
          >
            <Autocomplete
              sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
              options={options}
              getOptionLabel={(option: any) => option[labelKey] || ''}
              getOptionDisabled={(option) => !!option.disabled}
              isOptionEqualToValue={(option, value) => option[valueKey] === value[valueKey]}
              value={
                options.find(
                  (opt) => opt[valueKey] === field.value || opt[valueKey] === field.value?.[0]?.key,
                ) || null
              }
              onChange={(event, newValue) => {
                const selectedOption = newValue ? newValue[valueKey] : ''
                field.onChange(selectedOption)
                if (onChange) onChange({ target: { name, value: selectedOption } })
              }}
              ListboxProps={{
                style: searchOnly
                  ? {
                      maxHeight: '200px',
                      padding: '0',
                    }
                  : {},
              }}
              componentsProps={{
                popper: {
                  sx: searchOnly
                    ? {
                        '& .MuiPaper-root': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          borderRadius: '8px',
                          marginTop: '8px',
                        },
                        '& .MuiAutocomplete-option': {
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '10px 16px',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                          },
                        },
                      }
                    : {},
                },
              }}
              renderOption={(props, option) => {
                const statusValue = statusKey ? option?.[statusKey] : undefined
                const isInactive =
                  typeof statusValue === 'string' && statusValue.toUpperCase() === 'INACTIVE'
                return (
                  <li
                    {...props}
                    style={{
                      ...(searchOnly ? { padding: '8px 16px', fontSize: '14px' } : {}),
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{option?.[labelKey] ?? option}</span>
                    {statusValue !== undefined && (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 8,
                          flexShrink: 0,
                        }}
                      >
                        {statusLabel && (
                          <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {statusLabel} •
                          </span>
                        )}
                        <ChipButton
                          type={isInactive ? 'secondary' : 'success'}
                          label={isInactive ? 'Inactive' : 'Active'}
                          style={{ padding: '0px 8px' }}
                        />
                      </span>
                    )}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  label={
                    !searchOnly ? (
                      <span>
                        {label}
                        {required && <span style={{ color: '#D4351C' }}>*</span>}
                      </span>
                    ) : null
                  }
                  error={!!error?.[name]}
                  helperText={error?.[name]?.message as string}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {startIcon}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={textFieldStyle}
                  defaultValue={options?.length > 0 ? options?.[defaultValueIndex]?.value : ''}
                />
              )}
              disabled={disabled}
              popupIcon={searchOnly ? null : <KeyboardArrowDownIcon />}
            />
          </FormControl>
        )
      }}
    />
  )
}

export default MUIAutocompleteSingleSelect
